/**
 * Frequency bars list.
 *
 * Compact table showing values with inline bar charts, counts, and percentages.
 */

import React from 'react';
import { useTheme, fontSize, fontFamily, spacing } from '@fti/design-system';
import type { FrequencyBarsProps } from '../contracts';

export function FrequencyBars({ items, total, onItemClick }: FrequencyBarsProps) {
  const { theme } = useTheme();

  if (items.length === 0) {
    return (
      <div style={{ padding: spacing.sm, color: theme.colors.text.secondary, fontSize: fontSize.sm }}>
        No values
      </div>
    );
  }

  const maxCount = Math.max(...items.map((item) => item.count), 1);
  const clickable = !!onItemClick;

  return (
    <div style={{ fontSize: fontSize.xs }}>
      {items.map((item, i) => {
        const pct = ((item.count / total) * 100).toFixed(1);
        const barWidth = (item.count / maxCount) * 100;

        const rowStyle: React.CSSProperties = {
          display: 'flex',
          alignItems: 'center',
          gap: spacing.sm,
          padding: `6px ${spacing.sm}`,
          borderBottom: i < items.length - 1 ? `1px solid ${theme.colors.border.default}` : undefined,
          cursor: clickable ? 'pointer' : undefined,
          transition: clickable ? 'background 0.15s' : undefined,
        };

        const valueStyle: React.CSSProperties = {
          flex: '0 0 auto',
          minWidth: 0,
          maxWidth: '140px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontFamily: fontFamily.mono,
          color: theme.colors.text.primary,
        };

        const barContainerStyle: React.CSSProperties = {
          flex: '1 1 auto',
          minWidth: 0,
          height: '16px',
          background: theme.colors.primary[200],
          borderRadius: '2px',
          position: 'relative',
          overflow: 'hidden',
        };

        const barFillStyle: React.CSSProperties = {
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: `${barWidth}%`,
          background: theme.colors.primary[500],
        };

        const statsStyle: React.CSSProperties = {
          flex: '0 0 auto',
          display: 'flex',
          gap: spacing.xs,
          color: theme.colors.text.secondary,
        };

        if (clickable) {
          return (
            <button
              key={i}
              type="button"
              style={{ ...rowStyle, border: 'none', background: 'transparent', width: '100%', textAlign: 'left' }}
              onClick={() => onItemClick(item.value)}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = theme.colors.neutral[100];
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              <span style={valueStyle} title={item.value}>
                {item.value}
              </span>
              <div style={barContainerStyle}>
                <div style={barFillStyle} />
              </div>
              <span style={statsStyle}>
                <span>{item.count}</span>
                <span>({pct}%)</span>
              </span>
            </button>
          );
        }

        return (
          <div key={i} style={rowStyle}>
            <span style={valueStyle} title={item.value}>
              {item.value}
            </span>
            <div style={barContainerStyle}>
              <div style={barFillStyle} />
            </div>
            <span style={statsStyle}>
              <span>{item.count}</span>
              <span>({pct}%)</span>
            </span>
          </div>
        );
      })}
    </div>
  );
}
